import { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, X, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

const PROMPTS = [
  { key: 'surprised', label: 'What surprised you?', placeholder: 'Anything unexpected?' },
  { key: 'learned', label: 'What did you learn?', placeholder: 'A new insight, fact or feeling…' },
  { key: 'differently', label: 'What would you do differently?', placeholder: 'Looking back, anything you’d change?' },
];

// Optional post-completion reflection modal (iter-26 V2).
// Skippable — calling onSkip closes without persisting.
export default function MissionReflectionModal({ missionId, onClose, onSaved }) {
  const [vals, setVals] = useState({ surprised: '', learned: '', differently: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const body = Object.fromEntries(
      Object.entries(vals).map(([k, v]) => [k, (v || '').trim()])
    );
    if (!body.surprised && !body.learned && !body.differently) {
      // Nothing to save — treat as skip
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      await api.post(`/missions/${missionId}/reflection`, body);
      toast.success('Reflection saved — your mentor will use this');
      onSaved?.();
    } catch (e) {
      console.warn('reflection save failed', e?.response?.status, e?.message);
      toast.error('Could not save reflection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
      onClick={() => !saving && onClose?.()}
      data-testid="reflection-modal"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-6 border-b-4 border-[#E5E5E5] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <div>
            <p className="text-[10px] uppercase font-extrabold text-[#AFAFAF] tracking-wider">Optional</p>
            <h3 className="text-xl font-extrabold">Quick reflection</h3>
          </div>
          <button onClick={onClose} className="p-1" data-testid="reflection-close" aria-label="Close">
            <X size={24} strokeWidth={3} />
          </button>
        </div>
        <p className="text-sm font-bold text-[#4B4B4B] mb-4">
          One line each — your mentor remembers these and uses them to design better missions.
        </p>

        {PROMPTS.map((p) => (
          <div key={p.key} className="mb-3">
            <label className="block text-xs uppercase font-extrabold text-[#AFAFAF]">{p.label}</label>
            <textarea
              value={vals[p.key]}
              onChange={(e) => setVals((s) => ({ ...s, [p.key]: e.target.value }))}
              rows={2}
              maxLength={600}
              className="gm-input mt-2 text-base"
              placeholder={p.placeholder}
              data-testid={`reflection-${p.key}`}
            />
          </div>
        ))}

        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            disabled={saving}
            className="gm-btn gm-btn-secondary flex-1 py-3"
            data-testid="reflection-skip"
          >
            Skip
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="gm-btn gm-btn-primary flex-1 py-3"
            data-testid="reflection-save"
          >
            {saving ? (
              <><Loader2 size={18} strokeWidth={3} className="animate-spin" /> Saving…</>
            ) : (
              <><Sparkles size={18} strokeWidth={3} /> Save</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
